import type { BoundingBox } from '../../types';

export interface CameraSize {
  width: number;
  height: number;
}

export interface CameraFrameMetadata {
  sourceSize: CameraSize;
  viewportSize: CameraSize;
  sourceCrop: BoundingBox;
  outputSize: CameraSize;
  mirrored: boolean;
  objectFit: 'cover';
}

export interface CapturedCameraFrame {
  blob: Blob;
  metadata: CameraFrameMetadata;
}

const isPositiveSize = (size: CameraSize) =>
  Number.isFinite(size.width) && Number.isFinite(size.height) && size.width > 0 && size.height > 0;

const clamp = (value: number, minimum: number, maximum: number) =>
  Math.min(maximum, Math.max(minimum, value));

/**
 * Returns the source-pixel crop used by CSS `object-fit: cover` with a centered
 * object position. Keeping this calculation shared makes preview, capture, HUD,
 * and future ROI sampling agree on the exact visible frame.
 */
export function getCoverCrop(sourceSize: CameraSize, viewportSize: CameraSize): BoundingBox {
  if (!isPositiveSize(sourceSize) || !isPositiveSize(viewportSize)) {
    throw new Error('相機來源與取景框尺寸必須大於零');
  }

  const scale = Math.max(
    viewportSize.width / sourceSize.width,
    viewportSize.height / sourceSize.height,
  );
  const width = viewportSize.width / scale;
  const height = viewportSize.height / scale;

  return {
    x: (sourceSize.width - width) / 2,
    y: (sourceSize.height - height) / 2,
    width,
    height,
  };
}

export function createCameraFrameMetadata(
  sourceSize: CameraSize,
  viewportSize: CameraSize,
  mirrored: boolean,
): CameraFrameMetadata {
  const sourceCrop = getCoverCrop(sourceSize, viewportSize);
  const viewportAspect = viewportSize.width / viewportSize.height;
  let outputWidth: number;
  let outputHeight: number;

  if (sourceCrop.width < sourceSize.width - 0.001) {
    outputHeight = Math.max(1, Math.round(sourceCrop.height));
    outputWidth = Math.max(1, Math.round(outputHeight * viewportAspect));
  } else {
    outputWidth = Math.max(1, Math.round(sourceCrop.width));
    outputHeight = Math.max(1, Math.round(outputWidth / viewportAspect));
  }

  return {
    sourceSize: { ...sourceSize },
    viewportSize: { ...viewportSize },
    sourceCrop,
    outputSize: { width: outputWidth, height: outputHeight },
    mirrored,
    objectFit: 'cover',
  };
}

/** Maps a detector box in source-normalized coordinates into viewport CSS pixels. */
export function mapNormalizedRectToCoverViewport(
  normalizedRect: BoundingBox,
  sourceSize: CameraSize,
  viewportSize: CameraSize,
  mirrored = false,
): BoundingBox | null {
  if (!isPositiveSize(sourceSize) || !isPositiveSize(viewportSize)) return null;
  if (
    !Number.isFinite(normalizedRect.x)
    || !Number.isFinite(normalizedRect.y)
    || !Number.isFinite(normalizedRect.width)
    || !Number.isFinite(normalizedRect.height)
    || normalizedRect.width <= 0
    || normalizedRect.height <= 0
  ) return null;

  const crop = getCoverCrop(sourceSize, viewportSize);
  const scale = viewportSize.width / crop.width;
  const width = normalizedRect.width * sourceSize.width * scale;
  const height = normalizedRect.height * sourceSize.height * scale;
  let x = (normalizedRect.x * sourceSize.width - crop.x) * scale;
  const y = (normalizedRect.y * sourceSize.height - crop.y) * scale;

  if (mirrored) x = viewportSize.width - x - width;

  if (x + width <= 0 || y + height <= 0 || x >= viewportSize.width || y >= viewportSize.height) {
    return null;
  }

  return { x, y, width, height };
}

/**
 * Expands a mapped detector rectangle into a true CSS-pixel square and keeps it
 * inside the visible viewport. The detector's center remains the visual anchor.
 */
export function fitSquareInViewport(
  rect: BoundingBox,
  viewportSize: CameraSize,
  minimumSize = 32,
): BoundingBox | null {
  if (!isPositiveSize(viewportSize)) return null;
  if (rect.width <= 0 || rect.height <= 0) return null;

  const size = Math.min(
    Math.max(rect.width, rect.height, minimumSize),
    viewportSize.width,
    viewportSize.height,
  );
  const centerX = rect.x + rect.width / 2;
  const centerY = rect.y + rect.height / 2;
  const x = clamp(centerX - size / 2, 0, viewportSize.width - size);
  const y = clamp(centerY - size / 2, 0, viewportSize.height - size);

  return { x, y, width: size, height: size };
}

export function mapFaceBoxToViewportSquare(
  normalizedRect: BoundingBox,
  sourceSize: CameraSize,
  viewportSize: CameraSize,
  mirrored = false,
  minimumSize = 32,
): BoundingBox | null {
  const mapped = mapNormalizedRectToCoverViewport(
    normalizedRect,
    sourceSize,
    viewportSize,
    mirrored,
  );
  return mapped ? fitSquareInViewport(mapped, viewportSize, minimumSize) : null;
}
